<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import { onMounted, reactive, ref } from 'vue'
    
    const props = defineProps({
        userId: {
            type: Number,
            required: true
        },
        relationshipUserId: {
            type: Number,
            required: true
        },
        apiToken: {
            type: String,
            required: true
        }
    });

    const relationshipExists = reactive({
        FRIEND: false,
        FOLLOW: false
    });

    const accepted = ref(null);

    const isRequest = ref(null);

    const loading = ref(true);

    const getRelationship = type => {
        loading.value = true;
        axios.get(`/api/user-relationships/${props.relationshipUserId}?api_token=` + props.apiToken, {
            params: {
                'user_id': props.userId,
                'relationship_user_id': props.relationshipUserId,
                'type': type
            }
        }).then(response => {
            if (response.data) {
                if (type === 'FRIEND') {
                    relationshipExists.FRIEND = true;
                    accepted.value = response.data.accepted == 1 ? true : false;
                    isRequest.value = response.data.user_id != props.userId ? true : false;
                } else if (type === 'FOLLOW') {
                    relationshipExists.FOLLOW = true;
                }
            }
            loading.value = false;
        });
    }

    const sendFriendRequestOrFollow = type => {
        axios.post('/api/user-relationships?api_token=' + props.apiToken, {
            'user_id': props.userId,
            'relationship_user_id': props.relationshipUserId,
            'type': type
        }).then(response => {
            if (response.data) {
                if (type === 'FRIEND') {
                    relationshipExists.FRIEND = true;
                    relationshipExists.FOLLOW = true;
                    accepted.value = response.data.accepted == 1 ? true : false;
                    isRequest.value = false;
                } else if (type === 'FOLLOW') {
                    relationshipExists.FOLLOW = true;
                }
            }
            loading.value = false;
        });
    }

    const acceptFriendRequest = () => {
        axios.put(`/api/user-relationships/${props.relationshipUserId}?api_token=` + props.apiToken, {
            'user_id': props.userId,
            'type': 'FRIEND'
        }).then(response => {
            if (response.data && response.data == 1) {
                relationshipExists.FRIEND = true;
                accepted.value = true;
            }
            loading.value = false;
        });
    }

    const removeRelationship = type => {
        axios.delete(`/api/user-relationships/${props.relationshipUserId}?api_token=` + props.apiToken, {
            params: {
                'user_id': props.userId,
                'type': type
            }
        }).then(response => {
            if (!response.data) {
                if (type === 'FRIEND') {
                    relationshipExists.FRIEND = false;
                } else if (type === 'FOLLOW') {
                    relationshipExists.FOLLOW = false;
                }
            }
            loading.value = false;
        });
    }

    onMounted(() => {
        getRelationship('FRIEND');
        getRelationship('FOLLOW');
    });
</script>

<template>
    <span v-if="!loading" class="user-relationship-buttons">
        <template v-if="relationshipExists.FRIEND">
            <div v-if="accepted" class="dropdown">
                <button class="btn btn-sm btn-success dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                    &#10003; <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" style="fill: white; height: 15px"><path d="M192 256c61.9 0 112-50.1 112-112S253.9 32 192 32 80 82.1 80 144s50.1 112 112 112zm76.8 32h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C51.6 288 0 339.6 0 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2zM480 256c53 0 96-43 96-96s-43-96-96-96-96 43-96 96 43 96 96 96zm48 32h-3.8c-13.9 4.8-28.6 8-44.2 8s-30.3-3.2-44.2-8H432c-20.4 0-39.2 5.9-55.7 15.4 24.4 26.3 39.7 61.2 39.7 99.8v38.4c0 2.2-.5 4.3-.6 6.4H592c26.5 0 48-21.5 48-48 0-61.9-50.1-112-112-112z"/></svg>
                </button>
                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <button @click="removeRelationship('FRIEND')" class="dropdown-item">{{ __('Remove Friend') }}</button>
                </div>
            </div>
            <template v-else-if="isRequest">
                <button @click="acceptFriendRequest('FRIEND')" class="btn btn-warning btn-sm">{{ __('Confirm Friend') }}</button>
                <button @click="removeRelationship('FRIEND')" class="btn btn-danger btn-sm">{{ __('Decline Friend') }}</button>
            </template>
            <template v-else>
                <button class="btn btn-secondary btn-sm" disabled>{{ __('Friend Pending') }}</button>
                <button @click="removeRelationship('FRIEND')" class="btn btn-danger btn-sm">{{ __('Cancel Friend') }}</button>
            </template>
        </template>
        <template v-else>
            <button @click="sendFriendRequestOrFollow('FRIEND')" class="btn btn-primary btn-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" style="fill: white; height: 15px"><path d="M624 208h-64v-64c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v64h-64c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h64v64c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-64h64c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zm-400 48c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>
            </button>
        </template>
        <button v-if="relationshipExists.FOLLOW" @click="removeRelationship('FOLLOW')" class="btn btn-success btn-sm">&#10003; {{ __('Following') }}</button>
        <button v-else @click="sendFriendRequestOrFollow('FOLLOW')" class="btn btn-primary btn-sm">{{ __('Follow') }}</button>
    </span>
</template>